'use client';
import { PrimaryButton } from '@/components/buttons';
import { LinkButton } from '@/components/link-button';
import { Typography } from '@/components/typography/typography';
import { Variants, motion, useReducedMotion, useScroll } from 'framer-motion';
import { useRef } from 'react';

import useMediaQuery from 'hooks/useMediaQuery';

import { COLLAGE_COUNT, CollageCard } from './CollageCard';
import { HeroColumn } from './HeroColumn';
import { Developer, toColumns } from './developers';
import styles from './developersHero.module.scss';

const COLUMNS = 3;
const COLUMN_DURATIONS = [46, 58, 50];

export interface DevelopersHeroProps {
  title?: string;
  subtitle?: string;
  description?: string;
  primaryCta?: { label: string; href: string };
  secondaryCta?: { label: string; href: string };
  items?: Developer[];
}

const contentVariants: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.2, 0.7, 0.7, 1] },
  },
};

export const DevelopersHero = ({
  title = 'Top developers',
  subtitle = 'voor elk project',
  description = 'Snel personeel nodig of een developer voor de lange termijn? [code]capi is dé specialist voor detachering van ervaren developers om je project en team mee te upgraden.',
  primaryCta = { label: 'Hoe we je helpen', href: '#hoe-we-je-helpen' },
  secondaryCta = { label: 'Get in touch', href: '/contact' },
  items = [],
}: DevelopersHeroProps) => {
  const prefersReducedMotion = useReducedMotion();
  const columns = toColumns(items, COLUMNS);
  const collageItems = items.slice(0, COLLAGE_COUNT);
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const showGrid = isDesktop;

  const showCollage = !isDesktop;

  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  return (
    <section className={styles.hero} ref={heroRef}>
      {showCollage && (
        <div className={styles.collage} aria-hidden>
          {collageItems.map((dev, index) => (
            <CollageCard
              key={`${dev.firstName}-${dev.lastName}-${index}`}
              dev={dev}
              index={index}
              progress={scrollYProgress}
              reducedMotion={!!prefersReducedMotion}
            />
          ))}
        </div>
      )}

      <motion.div
        className={`${styles.content} lg:justify-self-center lg:w-fit`}
        variants={contentVariants}
        initial={prefersReducedMotion ? false : 'hidden'}
        animate="visible"
      >
        <motion.div className={styles.titleWrapper} variants={itemVariants}>
          <Typography as="h1" className="uppercase text-xl lg:text-8xl">
            <span className={styles.titleLine}>{title}</span>
            <span className={styles.titleLine}>{subtitle}</span>
          </Typography>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Typography
            as="p"
            className="lg:text-body-2xl text-neutral-600 text-body-md lg:max-w-[40rem]"
          >
            {description}
          </Typography>
        </motion.div>

        <motion.div className={styles.actions} variants={itemVariants}>
          <PrimaryButton href={primaryCta.href}>
            {primaryCta.label}
          </PrimaryButton>
          <LinkButton href={secondaryCta.href} label={secondaryCta.label} />
        </motion.div>
      </motion.div>

      {showGrid && (
        <motion.div
          className={styles.grid}
          initial={prefersReducedMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          aria-hidden
        >
          {columns.map((column, columnIndex) => (
            <HeroColumn
              key={columnIndex}
              column={column}
              columnIndex={columnIndex}
              duration={COLUMN_DURATIONS[columnIndex]}
            />
          ))}
        </motion.div>
      )}
    </section>
  );
};
